import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Copy, LogOut } from "lucide-react";
import { useWallet } from "../hooks/useWallet";
import { toast } from "sonner";
import WhaleLogo from "./WhaleLogo";
import SoundToggle from "./SoundToggle";
import { useAuth } from "../context/AuthContext";
import { usePrice } from "../hooks/usePrice";
import { fmtPrice, fmtChange } from "../lib/format";
import { BUY_URL, CA } from "../lib/constants";

const LINKS = [
  { id: "story", label: "Story" },
  { id: "chart", label: "Chart" },
  { id: "tokenomics", label: "Tokenomics" },
  { id: "how-to-buy", label: "How to Buy" },
  { id: "community", label: "Community" },
];

const PriceChip = () => {
  const { data } = usePrice();
  const change = fmtChange(data?.changeH24);
  const up = change && !change.startsWith("-");
  return (
    <div
      data-testid="nav-price-badge"
      className="sticker-sm hidden items-center gap-2 rounded-full bg-white px-3.5 py-2 sm:flex"
    >
      <span className="relative flex h-2.5 w-2.5">
        <span
          className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${up ? "bg-ocean-green" : "bg-ocean-coral"}`}
        />
        <span
          className={`relative inline-flex h-2.5 w-2.5 rounded-full ${up ? "bg-ocean-green" : "bg-ocean-coral"}`}
        />
      </span>
      <span className="font-display text-sm font-semibold">
        {data?.ok ? fmtPrice(data.priceUsd) : "…"}
      </span>
      {change && (
        <span
          className={`rounded-full px-1.5 py-0.5 text-[11px] font-extrabold ${up ? "bg-ocean-green/20 text-green-700" : "bg-ocean-coral/20 text-rose-700"}`}
        >
          {change}
        </span>
      )}
    </div>
  );
};

export const copyCa = async () => {
  const ok = () => toast.success("CA copied to clipboard — welcome to the pod!");
  try {
    await navigator.clipboard.writeText(CA);
    ok();
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = CA;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      ok();
    } catch {
      toast.error("Copy failed — select it manually");
    }
  }
};

export default function Navbar({ onNav }) {
  const [open, setOpen] = useState(false);
  const { user, openAuth, signOut } = useAuth();
  const { wallet, claim, claiming } = useWallet(user);

  return (
    <header className="fixed inset-x-0 top-3 z-50 px-3 sm:top-5 sm:px-6">
      <nav className="sticker mx-auto flex max-w-7xl items-center justify-between rounded-2xl bg-white/90 py-2.5 pl-4 pr-2.5 backdrop-blur-md">
        <button
          data-testid="nav-logo-button"
          onClick={() => window.scrollTo({ top: 0 })}
          className="flex items-center gap-2.5"
        >
          <WhaleLogo className="h-9 w-9" />
          <span className="hidden font-display text-xl font-semibold tracking-tight sm:inline">
            BLUEWHALE
          </span>
        </button>

        <div className="hidden items-center gap-6 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => onNav(l.id)}
              className="group relative whitespace-nowrap text-sm font-bold text-ocean-navy/70 transition-colors hover:text-ocean-navy"
            >
              {l.label}
              <span className="absolute -bottom-0.5 left-0 h-[3px] w-0 rounded-full bg-ocean-yellow transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          <PriceChip />
          <SoundToggle />
          {user ? (
            <>
              {wallet && (
                <div
                  data-testid="nav-balance-pill"
                  className="sticker-sm hidden items-center gap-1.5 rounded-full bg-ocean-navy px-3.5 py-2 text-white lg:flex"
                >
                  <WhaleLogo className="h-4 w-4" />
                  <span className="font-display text-sm font-semibold">
                    {(wallet.balance ?? 0).toLocaleString("en-US")} $BLUE
                  </span>
                </div>
              )}
              {wallet?.can_claim && (
                <button
                  data-testid="nav-claim-button"
                  onClick={() => claim.mutate()}
                  disabled={claiming}
                  className="btn-push sticker-sm hidden rounded-full bg-ocean-green px-3.5 py-2 font-display text-sm font-semibold lg:block"
                >
                  {claiming ? "…" : "Claim 10K"}
                </button>
              )}
              <div
                data-testid="nav-user-chip"
                className="sticker-sm hidden items-center gap-2 rounded-full bg-white py-1 pl-1.5 pr-2.5 lg:flex"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="h-7 w-7 rounded-full border-2 border-ocean-navy object-cover"
                  />
                ) : (
                  <WhaleLogo className="h-7 w-7" />
                )}
                <span className="max-w-[110px] truncate font-display text-sm font-semibold">
                  {user.name || user.email}
                </span>
                <button
                  data-testid="nav-logout-button"
                  onClick={signOut}
                  title="Sign out"
                  className="rounded-full p-1 transition-colors hover:bg-ocean-bg2"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </>
          ) : (
            <button
              data-testid="nav-signin-button"
              onClick={openAuth}
              className="btn-push sticker-sm hidden items-center rounded-full bg-ocean-coral px-4 py-2 font-display text-sm font-semibold sm:flex"
            >
              Sign In
            </button>
          )}
          <button
            data-testid="nav-copy-ca-button"
            onClick={copyCa}
            title="Copy contract address"
            className="btn-push sticker-sm hidden items-center gap-1.5 rounded-full bg-ocean-bg2 px-3 py-2 font-mono text-xs font-bold 2xl:flex"
          >
            <Copy className="h-3.5 w-3.5" />
            CA
          </button>
          <a
            data-testid="nav-buy-button"
            href={BUY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-push sticker-sm flex items-center whitespace-nowrap rounded-full bg-ocean-yellow px-4 py-2 font-display text-sm font-semibold"
          >
            Buy $BLUE
          </a>
          <button
            data-testid="nav-menu-toggle"
            className="sticker-sm rounded-xl p-2 lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="sticker mx-auto mt-2 max-w-7xl rounded-2xl bg-white p-4 lg:hidden"
          >
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => {
                  onNav(l.id);
                  setOpen(false);
                }}
                className="block w-full rounded-xl px-4 py-3 text-left font-display text-lg font-medium hover:bg-ocean-bg2"
              >
                {l.label}
              </button>
            ))}
            <div className="mt-2 border-t-[3px] border-ocean-navy/10 pt-3">
              {user ? (
                <div className="space-y-2 px-1">
                  <div
                    data-testid="nav-mobile-balance"
                    className="sticker-sm flex items-center justify-between rounded-xl bg-ocean-navy px-4 py-2.5 text-white"
                  >
                    <span className="font-display text-sm font-semibold">
                      {(wallet?.balance ?? 0).toLocaleString("en-US")} $BLUE
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/60">
                      pod balance
                    </span>
                  </div>
                  {wallet?.can_claim && (
                    <button
                      data-testid="nav-mobile-claim-button"
                      onClick={() => claim.mutate()}
                      disabled={claiming}
                      className="btn-push sticker-sm w-full rounded-xl bg-ocean-green px-4 py-2.5 font-display text-base font-semibold"
                    >
                      {claiming ? "Claiming…" : "Claim daily 10,000 $BLUE"}
                    </button>
                  )}
                  <button
                    data-testid="nav-mobile-logout-button"
                    onClick={() => {
                      signOut();
                      setOpen(false);
                    }}
                    className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-display text-base font-semibold text-ocean-muted"
                  >
                    <LogOut className="h-4 w-4" />
                    Log out ({user.name || user.email})
                  </button>
                </div>
              ) : (
                <button
                  data-testid="nav-mobile-signin-button"
                  onClick={() => {
                    setOpen(false);
                    openAuth();
                  }}
                  className="btn-push sticker-sm mx-1 flex w-[calc(100%-8px)] items-center justify-center rounded-xl bg-ocean-coral px-4 py-3 font-display text-lg font-semibold"
                >
                  Sign In
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
